import { exactRoot, iroot } from "@/lib/tool-kit/arithmetic/roots";
import { absBig, BIG_ONE, BIG_ZERO, gcdBig } from "@/lib/tool-kit/math/bigint";
import {
  factorize,
  MAX_FACTOR_INPUT,
  toSuperscript,
} from "@/lib/tool-kit/math/primes";
import { makeRational, type Rational } from "@/lib/tool-kit/math/rational";

/**
 * Roots in simplest radical form.
 *
 * Write the radicand as a product of prime powers, p^s with s negative for primes in the
 * denominator. For an index n, divide each exponent by n: s = n·t + r with 0 ≤ r < n. The
 * whole part t comes out of the root as a rational coefficient and the remainder r stays
 * under it, so that (a/b)^(1/n) = C · ⁿ√R with R an integer that has no n-th power
 * factor. Rationalising the denominator is automatic: √(1/2) = 1/2 · √2.
 */
export interface SimplifiedRadical {
  /** The rational number in front of the radical, positive. */
  coefficient: Rational;
  /** What stays under the radical: no factor appears n or more times. */
  radicand: bigint;
  /** False when a number was too big to factor completely. */
  complete: boolean;
}

/** Primes below this are tried by division when a number is too large to factorise. */
const PARTIAL_TRIAL_LIMIT = 10_000;

function floorDiv(a: number, b: number): number {
  return Math.floor(a / b);
}

function partialFactors(n: bigint): {
  map: Map<bigint, number>;
  complete: boolean;
} {
  const map = new Map<bigint, number>();
  let rest = n;
  for (let p = 2; p <= PARTIAL_TRIAL_LIMIT; p += p === 2 ? 1 : 2) {
    const prime = BigInt(p);
    let count = 0;
    while (rest % prime === BIG_ZERO) {
      rest /= prime;
      count += 1;
    }
    if (count > 0) {
      map.set(prime, count);
    }
  }
  if (rest > BIG_ONE) {
    map.set(rest, (map.get(rest) ?? 0) + 1);
  }
  // Nothing at or below the limit divides what is left, so a cofactor under limit² is prime.
  return { map, complete: rest < BigInt(PARTIAL_TRIAL_LIMIT) ** BigInt(2) };
}

function factorsOf(n: bigint): { map: Map<bigint, number>; complete: boolean } {
  if (n <= BIG_ONE) {
    return { map: new Map(), complete: true };
  }
  if (n > MAX_FACTOR_INPUT) {
    return partialFactors(n);
  }
  const { factors, complete } = factorize(n);
  return {
    map: new Map(factors.map((f) => [f.prime, f.exponent])),
    complete,
  };
}

/** ⁿ√(num/den) for num ≥ 1, den ≥ 1, in simplest radical form. */
export function simplifyNthRoot(x: Rational, n: number): SimplifiedRadical {
  const top = factorsOf(x.num);
  const bottom = factorsOf(x.den);
  const exponents = new Map<bigint, number>(top.map);
  for (const [prime, count] of bottom.map) {
    exponents.set(prime, (exponents.get(prime) ?? 0) - count);
  }
  let coefficientNum = BIG_ONE;
  let coefficientDen = BIG_ONE;
  let radicand = BIG_ONE;
  for (const [prime, s] of exponents) {
    const whole = floorDiv(s, n);
    const rest = s - whole * n;
    if (whole > 0) {
      coefficientNum *= prime ** BigInt(whole);
    } else if (whole < 0) {
      coefficientDen *= prime ** BigInt(-whole);
    }
    radicand *= prime ** BigInt(rest);
  }
  let complete = top.complete && bottom.complete;
  if (!complete) {
    // An unfactored cofactor can still be a perfect power; take it out if so.
    const root = exactRoot(radicand, n);
    if (root !== null) {
      coefficientNum *= root;
      radicand = BIG_ONE;
      complete = true;
    }
  }
  const divisor = gcdBig(coefficientNum, coefficientDen);
  return {
    coefficient: makeRational(
      coefficientNum / divisor,
      coefficientDen / divisor
    ),
    radicand,
    complete,
  };
}

const ROOT_SIGNS: Readonly<Record<number, string>> = { 2: "√", 3: "∛", 4: "∜" };

/** The radical sign for an index: √, ∛, ∜, then ⁵√, ⁶√ … */
export function radicalSign(n: number): string {
  return ROOT_SIGNS[n] ?? `${toSuperscript(n)}√`;
}

/** "6√2", "√2/2", "2∛3", "5": the simplified radical as text. */
export function formatSimplified(value: SimplifiedRadical, n: number): string {
  const { coefficient, radicand } = value;
  if (radicand === BIG_ONE) {
    return coefficient.den === BIG_ONE
      ? coefficient.num.toString()
      : `${coefficient.num}/${coefficient.den}`;
  }
  const front = coefficient.num === BIG_ONE ? "" : coefficient.num.toString();
  const tail = coefficient.den === BIG_ONE ? "" : `/${coefficient.den}`;
  return `${front}${radicalSign(n)}${radicand}${tail}`;
}

/* -------------------------------------------------------------- bounds */

/** The perfect n-th powers on either side of |x|: k^n ≤ |x| < (k+1)^n. */
export interface PowerBounds {
  root: bigint;
  lower: bigint;
  upper: bigint;
}

export function powerBounds(whole: bigint, n: number): PowerBounds {
  const k = iroot(absBig(whole), n);
  const exponent = BigInt(n);
  return { root: k, lower: k ** exponent, upper: (k + BIG_ONE) ** exponent };
}

/* --------------------------------------------------------------- Heron */

export interface HeronStep {
  step: number;
  estimate: number;
  square: number;
}

/**
 * Heron's (Newton's) method for √x: average a guess with x divided by the guess. Each step
 * roughly doubles the number of correct digits. Floating-point, so for x below 10^15.
 */
export function heronSteps(x: number, steps: number): HeronStep[] {
  const rows: HeronStep[] = [];
  let guess = Math.max(1, Math.floor(Math.sqrt(x)));
  if (guess * guess === x) {
    guess += 1;
  }
  for (let step = 0; step <= steps; step++) {
    rows.push({ step, estimate: guess, square: guess * guess });
    guess = (guess + x / guess) / 2;
  }
  return rows;
}
